import React, { useState } from 'react';
import { LicenseService, License } from '../../firebase/services/licenseService';

interface DomainManagerProps {
  license: License;
  onUpdate: () => void;
}

export default function DomainManager({ license, onUpdate }: DomainManagerProps) {
  const [newDomain, setNewDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const handleAddDomain = async () => {
    if (!newDomain.trim()) {
      setError('Please enter a domain');
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await LicenseService.addSiteToLicense(license.id!, newDomain.trim());
      setSuccess('Domain added successfully!');
      setNewDomain('');
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to add domain');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDomain = async (domain: string) => {
    if (!window.confirm(`Are you sure you want to remove ${domain} from this license?`)) {
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);

    try {
      await LicenseService.removeSiteFromLicense(license.id!, domain);
      setSuccess('Domain removed successfully!');
      onUpdate();
    } catch (err: any) {
      setError(err.message || 'Failed to remove domain');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mt-6 bg-gray-50 rounded-lg p-4">
      <h4 className="text-sm font-medium text-gray-900 mb-3">Manage Domains</h4>
      
      {error && (
        <div className="mb-3 bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}
      
      {success && (
        <div className="mb-3 bg-green-50 border border-green-200 rounded-md p-3">
          <p className="text-sm text-green-800">{success}</p>
        </div>
      )}

      <div className="flex items-center space-x-2 mb-4">
        <input
          type="text"
          value={newDomain}
          onChange={(e) => setNewDomain(e.target.value)}
          placeholder="Enter domain (e.g., example.com)"
          className="flex-1 border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500"
          disabled={loading}
        />
        <button
          onClick={handleAddDomain}
          disabled={loading || !newDomain.trim() || license.sites.length >= license.maxSites}
          className="px-4 py-2 bg-indigo-600 text-white text-sm font-medium rounded-md hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {loading ? 'Adding...' : 'Add Domain'}
        </button>
      </div>

      <div className="space-y-2">
        <div className="flex justify-between items-center">
          <span className="text-sm font-medium text-gray-700">
            Licensed Domains ({license.sites.length}/{license.maxSites})
          </span>
        </div>
        
        {license.sites.length === 0 ? (
          <p className="text-sm text-gray-500">No domains added yet.</p>
        ) : (
          <div className="space-y-2">
            {license.sites.map((domain, index) => (
              <div key={index} className="flex items-center justify-between bg-white rounded-md px-3 py-2 border">
                <span className="text-sm text-gray-900 font-mono">{domain}</span>
                <button
                  onClick={() => handleRemoveDomain(domain)}
                  disabled={loading}
                  className="text-sm text-red-600 hover:text-red-900 disabled:opacity-50"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
} 